/**
 * P-11 授权详情页。
 *
 * 不再是占位页。承载四块信息，全部来自 GET /admin/licenses/:id（LicenseDetailResponse）：
 * 1. 基本信息卡（definition-list + 权益标签）
 * 2. 生命周期时间线（由 License 自带的 issued/suspended/revoked/expires 时间戳诚实构建，
 *    不臆造审计事件）
 * 3. 激活设备表（可强制解绑，reason 写入审计）
 * 4. Danger Zone：按当前状态给出 suspend / revoke / resume / renew / 改备注，
 *    每个动作都走专用端点，且 revoke/suspend 必须把 expected_offline_expiry_at 显式呈现（PRD P0-4）。
 *
 * 严格处理三态：Loading（骨架）/ Error（含 404 不复用重试）/ Ready。
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyButton } from '../../components/CopyButton';
import { ErrorBlock, CardSkeleton } from '../../components/DataState';
import { Icon } from '../../icons/Icon';
import { ICON_SIZE, cssVar } from '../../tokens';
import type { IconName } from '../../icons/registry';
import type { CSSProperties } from 'react';
import { formatDateTime, formatExpiry, relativeTime, middleTruncate } from '../../lib/format';
import { useAsync, type AsyncState } from '../../lib/useAsync';
import { describeError } from '../../api/client';
import {
  getLicense,
  updateLicense,
  revokeLicense,
  suspendLicense,
  resumeLicense,
  renewLicense,
  listLicenseActivations,
  deactivateActivation,
  listProducts,
  type Activation,
} from '../../api/endpoints';
import type { ActivationStatus, EntitlementValue, License, LicenseStatus } from '../../api/types';

type DialogKind = 'suspend' | 'revoke' | 'resume' | 'renew' | 'note' | 'deactivate' | null;

interface Feedback {
  readonly kind: 'success' | 'warn';
  readonly message: string;
}

export function LicenseDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Activation | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [activationNonce, setActivationNonce] = useState(0);

  const detail = useAsync(useCallback(() => getLicense(id), [id]), [id]);
  const activations = useAsync(
    useCallback(() => listLicenseActivations(id, { includeDeactivated }), [id, includeDeactivated, activationNonce]),
    [id, includeDeactivated, activationNonce],
  );
  const products = useAsync(useCallback(() => listProducts(), []), []);

  const license = detail.state.kind === 'ready' ? detail.state.data : null;

  const productName = useMemo(() => {
    if (products.state.kind !== 'ready' || license === null) return null;
    return products.state.data.items.find((p) => p.id === license.product_id)?.name ?? null;
  }, [products.state, license]);

  const closeDialog = useCallback(() => {
    setDialog(null);
    setDeactivateTarget(null);
  }, []);

  const refreshAll = useCallback(() => {
    detail.reload();
    setActivationNonce((n) => n + 1);
  }, [detail]);

  const openDeactivate = useCallback((activation: Activation) => {
    setDeactivateTarget(activation);
    setDialog('deactivate');
  }, []);

  return (
    <div className="license-detail">
      <Link to="/licenses" className="link-button license-detail__back">
        返回授权列表
      </Link>

      {feedback && (
        <div className={`notice ${feedback.kind === 'warn' ? 'notice--warn' : ''}`} role="status">
          <span className="notice__icon">
            <Icon name={feedback.kind === 'warn' ? 'circle-alert' : 'circle-check'} size={ICON_SIZE.inline} />
          </span>
          <p>{feedback.message}</p>
        </div>
      )}

      {detail.state.kind === 'loading' ? (
        <>
          <CardSkeleton lines={4} />
          <div style={{ height: 'var(--space-4)' }} />
          <CardSkeleton lines={5} />
        </>
      ) : detail.state.kind === 'failed' ? (
        <ErrorBlock message={describeError(detail.state.error, '加载授权详情')} onRetry={detail.reload} />
      ) : license ? (
        <>
          <div className="page-head">
            <div>
              <h1 className="page-head__title">
                <span className="cell-inline">
                  <span className="cell-id" title={license.id}>
                    {middleTruncate(license.id, 12, 6)}
                  </span>
                  <CopyButton value={license.id} what=" license_id" />
                </span>
              </h1>
              <p className="page-head__sub">
                授权生命周期与设备激活明细。撤销或暂停会改变吊销列表版本号，客户端下次校验即生效。
              </p>
            </div>
            <div className="page-head__actions">
              <StatusBadge status={license.status} reason={license.status_reason} />
            </div>
          </div>

          <LicenseInfoCard license={license} productName={productName} onEditNote={() => setDialog('note')} />

          <LifecycleTimeline license={license} />

          <section className="card license-section">
            <div className="license-section__head">
              <h2 className="section-title">激活设备</h2>
              <label className="license-toggle">
                <input
                  type="checkbox"
                  checked={includeDeactivated}
                  onChange={(e) => setIncludeDeactivated(e.target.checked)}
                />
                显示已解绑设备
              </label>
            </div>
            <ActivationsTable
              state={activations.state}
              onRetry={activations.reload}
              onDeactivate={openDeactivate}
            />
          </section>

          <DangerZone
            license={license}
            onSuspend={() => setDialog('suspend')}
            onRevoke={() => setDialog('revoke')}
            onResume={() => setDialog('resume')}
            onRenew={() => setDialog('renew')}
            onEditNote={() => setDialog('note')}
          />

          {dialog === 'suspend' && (
            <ReasonDialog
              title="暂停授权"
              description="暂停后客户端将立即无法校验通过；可随时恢复。请填写原因（写入审计）。"
              confirmLabel="确认暂停"
              danger
              onClose={closeDialog}
              onSubmit={async (reason) => {
                const result = await suspendLicense(license.id, reason);
                const at = formatDateTime(result.expected_offline_expiry_at);
                setFeedback({
                  kind: 'warn',
                  message: `已暂停。在线客户端下次校验即失效；离线客户端最迟 ${at} 失效（宽限 ${String(result.offline_grace_seconds)}s）。`,
                });
                closeDialog();
                refreshAll();
              }}
            />
          )}

          {dialog === 'revoke' && (
            <ReasonDialog
              title="撤销授权"
              description="撤销是终态，不可恢复。在线客户端下次校验即失效；离线客户端在宽限期内仍可用。请填写原因（写入审计）。"
              confirmLabel="确认撤销"
              danger
              onClose={closeDialog}
              onSubmit={async (reason) => {
                const result = await revokeLicense(license.id, reason);
                const at = formatDateTime(result.expected_offline_expiry_at);
                setFeedback({
                  kind: 'warn',
                  message: `已撤销。在线客户端下次校验即失效；离线客户端最迟 ${at} 失效（宽限 ${String(result.offline_grace_seconds)}s）。`,
                });
                closeDialog();
                refreshAll();
              }}
            />
          )}

          {dialog === 'resume' && (
            <ConfirmDialog
              title="恢复授权"
              description="将把该授权从「已暂停」恢复为「活跃」。此操作不可用于已撤销或已过期授权。"
              confirmLabel="确认恢复"
              onClose={closeDialog}
              onSubmit={async () => {
                await resumeLicense(license.id);
                setFeedback({ kind: 'success', message: '已恢复为活跃状态。' });
                closeDialog();
                refreshAll();
              }}
            />
          )}

          {dialog === 'renew' && (
            <RenewDialog
              license={license}
              onClose={closeDialog}
              onSubmit={async (body) => {
                const renewed = await renewLicense(license.id, body);
                const expiry = formatExpiry(renewed.expires_at);
                setFeedback({ kind: 'success', message: `已续期，新到期时间：${expiry.text}。` });
                closeDialog();
                refreshAll();
              }}
            />
          )}

          {dialog === 'note' && (
            <NoteDialog
              initial={license.note}
              onClose={closeDialog}
              onSubmit={async (note) => {
                await updateLicense(license.id, { note });
                setFeedback({ kind: 'success', message: '备注已保存。' });
                closeDialog();
                refreshAll();
              }}
            />
          )}

          {dialog === 'deactivate' && deactivateTarget && (
            <ReasonDialog
              title="强制解绑设备"
              description={`将解绑设备「${deactivateTarget.device_name ?? deactivateTarget.device_hash}」。与客户端自助解绑同事务纪律，原因写入审计。`}
              confirmLabel="确认解绑"
              danger
              onClose={closeDialog}
              onSubmit={async (reason) => {
                await deactivateActivation(deactivateTarget.id, reason);
                setFeedback({ kind: 'success', message: '设备已解绑。' });
                closeDialog();
                setActivationNonce((n) => n + 1);
              }}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

/* ────────────────────────── 基本信息卡 ────────────────────────── */

function LicenseInfoCard({
  license,
  productName,
  onEditNote,
}: {
  license: License;
  productName: string | null;
  onEditNote: () => void;
}) {
  const expiry = formatExpiry(license.expires_at);
  const entries: Array<{ label: string; value: ReactNode }> = [
    { label: '产品', value: productName ?? <span className="cell-muted" title={license.product_id}>{middleTruncate(license.product_id, 8, 4)}</span> },
    { label: '套餐', value: `${license.plan_snapshot.plan_name}（${license.plan_snapshot.plan_code}）` },
    { label: '发行时间', value: formatDateTime(license.issued_at) },
    {
      label: '到期时间',
      value: (
        <span className={expiry.urgent ? 'cell-urgent' : undefined} title={expiry.title}>
          {expiry.text}
        </span>
      ),
    },
    { label: '设备', value: `${license.activated_device_count} / ${license.plan_snapshot.max_devices}` },
    { label: '离线宽限', value: `${String(license.plan_snapshot.offline_grace_seconds)} 秒` },
    {
      label: '版本约束',
      value: license.plan_snapshot.version_constraint ?? <span className="cell-muted">无</span>,
    },
    {
      label: '客户',
      value: license.customer_id ? (
        <Link to="/customers" className="link-button">
          {middleTruncate(license.customer_id, 8, 4)}
        </Link>
      ) : (
        <span className="cell-muted">未登记</span>
      ),
    },
    {
      label: '订单',
      value: license.order_id ? (
        <span className="cell-id" title={license.order_id}>
          {license.order_id}
        </span>
      ) : (
        <span className="cell-muted">—</span>
      ),
    },
    {
      label: '状态原因',
      value: license.status_reason ?? <span className="cell-muted">—</span>,
    },
    {
      label: '备注',
      value: license.note ?? <span className="cell-muted">—</span>,
    },
  ];

  const entitlements = Object.entries(license.plan_snapshot.entitlements);

  return (
    <section className="card license-section">
      <div className="license-section__head">
        <h2 className="section-title">基本信息</h2>
        <button type="button" className="btn btn--quiet" onClick={onEditNote}>
          <Icon name="pencil" size={ICON_SIZE.inline} />
          编辑备注
        </button>
      </div>
      <dl className="definition-list">
        {entries.map((entry) => (
          <div key={entry.label}>
            <dt>{entry.label}</dt>
            <dd>{entry.value}</dd>
          </div>
        ))}
      </dl>
      {entitlements.length > 0 && (
        <div className="license-entitlements">
          <span className="license-entitlements__label">权益快照</span>
          <div className="plan-card__entitlements">
            {entitlements.map(([key, value]) => (
              <span key={key} className="tag" title={`${key} = ${String(value)}`}>
                {key}: {renderEntitlement(value)}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function renderEntitlement(value: EntitlementValue): string {
  if (typeof value === 'boolean') return value ? '开' : '关';
  if (typeof value === 'number') return String(value);
  return value;
}

/* ────────────────────────── 生命周期时间线 ────────────────────────── */

interface TimelineEvent {
  readonly key: string;
  readonly icon: IconName;
  readonly label: string;
  readonly at: number;
  readonly tone: 'neutral' | 'warn' | 'danger' | 'success';
}

function LifecycleTimeline({ license }: { license: License }) {
  const now = Math.floor(Date.now() / 1000);
  const events: TimelineEvent[] = [
    { key: 'issued', icon: 'file-key', label: '授权发行', at: license.issued_at, tone: 'neutral' },
  ];
  if (license.suspended_at !== null) {
    events.push({ key: 'suspended', icon: 'circle-pause', label: '暂停', at: license.suspended_at, tone: 'warn' });
  }
  if (license.revoked_at !== null) {
    events.push({ key: 'revoked', icon: 'ban', label: '撤销', at: license.revoked_at, tone: 'danger' });
  }
  if (license.expires_at !== null) {
    const past = license.expires_at <= now;
    events.push({
      key: 'expires',
      icon: 'clock-alert',
      label: past ? '已到期' : '到期',
      at: license.expires_at,
      tone: past ? 'danger' : 'warn',
    });
  }
  events.sort((a, b) => a.at - b.at);

  return (
    <section className="card license-section">
      <h2 className="section-title">
        <span className="cell-inline">
          <Icon name="history" size={ICON_SIZE.inline} />
          生命周期
        </span>
      </h2>
      <ol className="timeline">
        {events.map((event) => {
          const rel = relativeTime(event.at, now);
          return (
            <li key={event.key} className="timeline__item">
              <span className={`timeline__dot timeline__dot--${event.tone}`}>
                <Icon name={event.icon} size={ICON_SIZE.badge} />
              </span>
              <div className="timeline__body">
                <div className="timeline__label">{event.label}</div>
                <div className="timeline__time" title={formatDateTime(event.at)}>
                  {formatDateTime(event.at)}
                  <span className="timeline__rel">{rel.text}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* ────────────────────────── 激活设备表 ────────────────────────── */

function ActivationsTable({
  state,
  onRetry,
  onDeactivate,
}: {
  state: AsyncState<Activation[]>;
  onRetry: () => void;
  onDeactivate: (activation: Activation) => void;
}) {
  if (state.kind === 'loading') {
    return (
      <div className="table-frame">
        <div className="state-block">
          <span className="state-block__icon">
            <Icon name="loader-circle" size={ICON_SIZE.standalone} spin />
          </span>
        </div>
      </div>
    );
  }
  if (state.kind === 'failed') {
    return <ErrorBlock message={describeError(state.error, '加载激活设备')} onRetry={onRetry} />;
  }
  if (state.data.length === 0) {
    return (
      <p className="text-muted" style={{ margin: 0 }}>
        该授权下暂无激活设备。
      </p>
    );
  }
  return (
    <div className="table-frame">
      <table className="data-table">
        <thead>
          <tr>
            <th>设备</th>
            <th>设备指纹</th>
            <th>状态</th>
            <th>激活时间</th>
            <th>最近心跳</th>
            <th className="cell--actions">操作</th>
          </tr>
        </thead>
        <tbody>
          {state.data.map((activation) => (
            <tr key={activation.id}>
              <td>{activation.device_name ?? <span className="cell-muted">未命名</span>}</td>
              <td>
                <span className="cell-id" title={activation.device_hash}>
                  {middleTruncate(activation.device_hash, 10, 6)}
                </span>
              </td>
              <td>
                <ActivationStatusBadge status={activation.status} />
              </td>
              <td>{formatDateTime(activation.activated_at)}</td>
              <td>{formatDateTime(activation.last_seen_at)}</td>
              <td className="cell--actions">
                {activation.status === 'active' ? (
                  <button
                    type="button"
                    className="btn btn--quiet btn--icon"
                    aria-label="强制解绑该设备"
                    title="强制解绑"
                    onClick={() => onDeactivate(activation)}
                  >
                    <Icon name="unplug" size={ICON_SIZE.inline} />
                  </button>
                ) : (
                  <span className="cell-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────────── 激活状态徽标 ────────────────────────── */
/**
 * 激活状态（active / deactivated）与授权状态（active/suspended/revoked/expired）是两套枚举，
 * StatusBadge 只覆盖后者。这里用同一套三通道（图标+文字+色）渲染激活状态，避免引入新组件套路。
 */
function ActivationStatusBadge({ status }: { status: ActivationStatus }) {
  const active = status === 'active';
  const style = { '--st': cssVar(active ? 'st-done' : 'st-void') } as CSSProperties;
  return (
    <span className="status-badge" style={style}>
      <Icon name={active ? 'circle-check' : 'circle-dot'} size={ICON_SIZE.badge} />
      {active ? '活跃' : '已解绑'}
    </span>
  );
}

/* ────────────────────────── Danger Zone ────────────────────────── */

function DangerZone({
  license,
  onSuspend,
  onRevoke,
  onResume,
  onRenew,
  onEditNote,
}: {
  license: License;
  onSuspend: () => void;
  onRevoke: () => void;
  onResume: () => void;
  onRenew: () => void;
  onEditNote: () => void;
}) {
  const status = license.status as LicenseStatus;
  if (status === 'revoked') {
    return (
      <section className="card danger-zone">
        <h2 className="danger-zone__title">危险操作</h2>
        <p className="danger-zone__desc">
          该授权已撤销，是不可恢复的终态，不再提供变更操作。
        </p>
      </section>
    );
  }

  return (
    <section className="card danger-zone">
      <h2 className="danger-zone__title">
        <span className="cell-inline">
          <Icon name="octagon-x" size={ICON_SIZE.inline} />
          危险操作
        </span>
      </h2>
      <p className="danger-zone__desc">
        以下操作会改变授权状态并写入审计，部分不可逆。请确认影响范围后再执行。
      </p>
      <div className="danger-actions">
        {status === 'active' && (
          <button type="button" className="btn" onClick={onSuspend}>
            <Icon name="pause" size={ICON_SIZE.inline} />
            暂停
          </button>
        )}
        {status === 'suspended' && (
          <button type="button" className="btn btn--primary" onClick={onResume}>
            <Icon name="play" size={ICON_SIZE.inline} />
            恢复
          </button>
        )}
        <button type="button" className="btn" onClick={onRenew}>
          <Icon name="repeat" size={ICON_SIZE.inline} />
          续期
        </button>
        <button type="button" className="btn" onClick={onEditNote}>
          <Icon name="pencil" size={ICON_SIZE.inline} />
          编辑备注
        </button>
        <button type="button" className="btn btn--danger" onClick={onRevoke}>
          <Icon name="ban" size={ICON_SIZE.inline} />
          撤销
        </button>
      </div>
    </section>
  );
}

/* ────────────────────────── 对话框 ────────────────────────── */

function DialogShell({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="panel-overlay" onClick={onClose}>
      <div
        className="panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="panel__head">
          <h2 className="panel__title">{title}</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          {children}
          {footer}
        </div>
      </div>
    </div>
  );
}

function ReasonDialog({
  title,
  description,
  confirmLabel,
  danger,
  onClose,
  onSubmit,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (!reason.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : '操作失败');
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      title={title}
      onClose={onClose}
      footer={
        <>
          {error && (
            <p className="login-error">
              <Icon name="octagon-x" size={ICON_SIZE.inline} />
              {error}
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              取消
            </button>
            <button
              type="button"
              className={danger ? 'btn btn--danger' : 'btn btn--primary'}
              disabled={submitting || !reason.trim()}
              onClick={handle}
            >
              {submitting ? '处理中…' : confirmLabel}
            </button>
          </div>
        </>
      }
    >
      <p className="text-muted">{description}</p>
      <div className="field">
        <label>原因（必填）</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="例如：客户申请暂停，等待发票核销"
        />
      </div>
    </DialogShell>
  );
}

function ConfirmDialog({
  title,
  description,
  confirmLabel,
  onClose,
  onSubmit,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit();
    } catch (e) {
      setError(e instanceof Error ? e.message : '操作失败');
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      title={title}
      onClose={onClose}
      footer={
        <>
          {error && (
            <p className="login-error">
              <Icon name="octagon-x" size={ICON_SIZE.inline} />
              {error}
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              取消
            </button>
            <button type="button" className="btn btn--primary" disabled={submitting} onClick={handle}>
              {submitting ? '处理中…' : confirmLabel}
            </button>
          </div>
        </>
      }
    >
      <p className="text-muted">{description}</p>
    </DialogShell>
  );
}

function NoteDialog({
  initial,
  onClose,
  onSubmit,
}: {
  initial: string | null;
  onClose: () => void;
  onSubmit: (note: string | null) => Promise<void>;
}) {
  const [note, setNote] = useState(initial ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(note.trim() === '' ? null : note.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存失败');
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      title="编辑备注"
      onClose={onClose}
      footer={
        <>
          {error && (
            <p className="login-error">
              <Icon name="octagon-x" size={ICON_SIZE.inline} />
              {error}
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              取消
            </button>
            <button type="button" className="btn btn--primary" disabled={submitting} onClick={handle}>
              {submitting ? '保存中…' : '保存'}
            </button>
          </div>
        </>
      }
    >
      <div className="field">
        <label>备注</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder="内部备注，仅管理员可见"
        />
      </div>
    </DialogShell>
  );
}

function RenewDialog({
  license,
  onClose,
  onSubmit,
}: {
  license: License;
  onClose: () => void;
  onSubmit: (body: { expires_at: number | null; refresh_plan_snapshot?: boolean; reason?: string | null }) => Promise<void>;
}) {
  const initialDate = toDateInput(license.expires_at ?? Math.floor(Date.now() / 1000) + 365 * 86_400);
  const [date, setDate] = useState(initialDate);
  const [perpetual, setPerpetual] = useState(license.expires_at === null);
  const [refresh, setRefresh] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        expires_at: perpetual ? null : fromDateInput(date),
        refresh_plan_snapshot: refresh,
        reason: reason.trim() === '' ? null : reason.trim(),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : '续期失败');
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      title="续期授权"
      onClose={onClose}
      footer={
        <>
          {error && (
            <p className="login-error">
              <Icon name="octagon-x" size={ICON_SIZE.inline} />
              {error}
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              取消
            </button>
            <button type="button" className="btn btn--primary" disabled={submitting} onClick={handle}>
              {submitting ? '续期中…' : '确认续期'}
            </button>
          </div>
        </>
      }
    >
      <p className="text-muted">
        允许延长（active→active）或复活（expired→active）。默认不刷新权益快照，避免把老授权权益改成当前套餐值。
      </p>
      <label className="license-toggle license-toggle--block">
        <input type="checkbox" checked={perpetual} onChange={(e) => setPerpetual(e.target.checked)} />
        转为永久授权（无到期时间）
      </label>
      {!perpetual && (
        <div className="field">
          <label>新到期日期</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      )}
      <label className="license-toggle license-toggle--block">
        <input type="checkbox" checked={refresh} onChange={(e) => setRefresh(e.target.checked)} />
        同步刷新权益快照（显式操作，写审计）
      </label>
      <div className="field">
        <label>原因（可选）</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="续期原因，写入审计"
        />
      </div>
    </DialogShell>
  );
}

/* ── 日期输入转换（本地时区，epoch 秒） ── */

function toDateInput(ts: number): string {
  const d = new Date(ts * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromDateInput(value: string): number {
  return Math.floor(new Date(`${value}T00:00:00`).getTime() / 1000);
}
